import React, { useState } from 'react';
import Logo from '../components/Logo/Logo';

const LogoDownload: React.FC = () => {
  const [selectedSize, setSelectedSize] = useState<'small' | 'medium' | 'large'>('medium');

  const downloadLogoWithOptions = (includeText: boolean, bgColor: 'gradient' | 'white' | 'transparent', size: number) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) return;

    // Set canvas size
    const padding = includeText ? 40 : 20;
    const textWidth = includeText ? size * 3 : 0;
    canvas.width = size + textWidth + (includeText ? padding * 2 : 0);
    canvas.height = size;

    // Draw background
    if (bgColor === 'white') {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    } else if (bgColor === 'gradient') {
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, '#1F2937'); // gray-800
      gradient.addColorStop(1, '#111827'); // gray-900
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    // transparent - no background needed

    // Draw logo background
    const logoX = includeText ? padding : (canvas.width - size) / 2;
    const logoY = 0;
    const gradient = ctx.createLinearGradient(logoX, logoY, logoX + size, logoY + size);
    gradient.addColorStop(0, '#3B82F6'); // blue-500
    gradient.addColorStop(1, '#9333EA'); // purple-600
    
    // Draw rounded rectangle
    const borderRadius = size * 0.15;
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.moveTo(logoX + borderRadius, logoY);
    ctx.lineTo(logoX + size - borderRadius, logoY);
    ctx.quadraticCurveTo(logoX + size, logoY, logoX + size, logoY + borderRadius);
    ctx.lineTo(logoX + size, logoY + size - borderRadius);
    ctx.quadraticCurveTo(logoX + size, logoY + size, logoX + size - borderRadius, logoY + size);
    ctx.lineTo(logoX + borderRadius, logoY + size);
    ctx.quadraticCurveTo(logoX, logoY + size, logoX, logoY + size - borderRadius);
    ctx.lineTo(logoX, logoY + borderRadius);
    ctx.quadraticCurveTo(logoX, logoY, logoX + borderRadius, logoY);
    ctx.closePath();
    ctx.fill();

    // Draw lightbulb icon
    const centerX = logoX + size / 2;
    const centerY = logoY + size / 2;
    const bulbRadius = size * 0.15;
    
    ctx.strokeStyle = 'white';
    ctx.lineWidth = size * 0.04;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    // Bulb shape
    ctx.beginPath();
    ctx.arc(centerX, centerY - bulbRadius * 0.3, bulbRadius, Math.PI * 1.2, Math.PI * -0.2);
    ctx.stroke();
    
    // Bulb base
    const baseY = centerY + bulbRadius * 0.7;
    ctx.beginPath();
    ctx.moveTo(centerX - bulbRadius * 0.5, baseY);
    ctx.lineTo(centerX + bulbRadius * 0.5, baseY);
    ctx.stroke();
    
    // Filament lines
    ctx.beginPath();
    ctx.moveTo(centerX - bulbRadius * 0.3, baseY + bulbRadius * 0.2);
    ctx.lineTo(centerX - bulbRadius * 0.3, baseY + bulbRadius * 0.4);
    ctx.moveTo(centerX, baseY + bulbRadius * 0.2);
    ctx.lineTo(centerX, baseY + bulbRadius * 0.4);
    ctx.moveTo(centerX + bulbRadius * 0.3, baseY + bulbRadius * 0.2);
    ctx.lineTo(centerX + bulbRadius * 0.3, baseY + bulbRadius * 0.4);
    ctx.stroke();
    
    // Light rays
    const rayLength = bulbRadius * 0.4;
    const rays = [
      { x: 0, y: -1 },
      { x: 0.707, y: -0.707 },
      { x: 1, y: 0 },
      { x: 0.707, y: 0.707 },
      { x: -0.707, y: -0.707 },
      { x: -1, y: 0 },
      { x: -0.707, y: 0.707 }
    ];
    
    rays.forEach(ray => {
      const startX = centerX + (bulbRadius + rayLength * 0.5) * ray.x;
      const startY = centerY - bulbRadius * 0.3 + (bulbRadius + rayLength * 0.5) * ray.y;
      const endX = centerX + (bulbRadius + rayLength * 1.2) * ray.x;
      const endY = centerY - bulbRadius * 0.3 + (bulbRadius + rayLength * 1.2) * ray.y;
      
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.stroke();
    });

    // Draw text if needed
    if (includeText) {
      ctx.fillStyle = bgColor === 'white' ? '#111827' : 'white';
      ctx.font = `bold ${size * 0.4}px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
      ctx.textBaseline = 'middle';
      ctx.fillText('PM Coach', logoX + size + padding / 2, canvas.height / 2);
    }

    // Download
    canvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const bgName = bgColor === 'transparent' ? 'transparent' : bgColor;
        const textName = includeText ? '-with-text' : '';
        a.download = `pm-coach-logo-${size}x${size}${textName}-${bgName}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    }, 'image/png');
  };

  const sizes = {
    small: 256,
    medium: 512,
    large: 1024
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 pt-20">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">Download PM Coach Logo</h1>
          <p className="text-gray-300">Choose your preferred logo format and download as PNG</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Logo Only - Gradient Background */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex justify-center items-center h-40 bg-gradient-to-br from-gray-700 to-gray-900 rounded-lg mb-4">
              <Logo width={80} height={80} />
            </div>
            <h3 className="text-white font-semibold mb-2">Logo Icon - Dark</h3>
            <p className="text-gray-400 text-sm mb-4">Square logo with gradient background</p>
            <button
              onClick={() => downloadLogoWithOptions(false, 'gradient', sizes[selectedSize])}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
            >
              Download PNG
            </button>
          </div>

          {/* Logo Only - White Background */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex justify-center items-center h-40 bg-white rounded-lg mb-4">
              <Logo width={80} height={80} />
            </div>
            <h3 className="text-white font-semibold mb-2">Logo Icon - Light</h3>
            <p className="text-gray-400 text-sm mb-4">Square logo with white background</p>
            <button
              onClick={() => downloadLogoWithOptions(false, 'white', sizes[selectedSize])}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
            >
              Download PNG
            </button>
          </div>

          {/* Logo Only - Transparent */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex justify-center items-center h-40 bg-gray-700 rounded-lg mb-4" style={{
              backgroundImage: 'repeating-conic-gradient(#808080 0% 25%, transparent 0% 50%)',
              backgroundPosition: '0 0, 10px 10px',
              backgroundSize: '20px 20px'
            }}>
              <Logo width={80} height={80} />
            </div>
            <h3 className="text-white font-semibold mb-2">Logo Icon - Transparent</h3>
            <p className="text-gray-400 text-sm mb-4">Square logo with transparent background</p>
            <button
              onClick={() => downloadLogoWithOptions(false, 'transparent', sizes[selectedSize])}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
            >
              Download PNG
            </button>
          </div>

          {/* Logo with Text - Dark */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex justify-center items-center h-40 bg-gradient-to-br from-gray-700 to-gray-900 rounded-lg mb-4">
              <Logo width={60} height={60} showText={true} textClassName="text-white" />
            </div>
            <h3 className="text-white font-semibold mb-2">Full Logo - Dark</h3>
            <p className="text-gray-400 text-sm mb-4">Logo with text on dark background</p>
            <button
              onClick={() => downloadLogoWithOptions(true, 'gradient', sizes[selectedSize])}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
            >
              Download PNG
            </button>
          </div>

          {/* Logo with Text - Light */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex justify-center items-center h-40 bg-white rounded-lg mb-4">
              <Logo width={60} height={60} showText={true} textClassName="text-gray-900" />
            </div>
            <h3 className="text-white font-semibold mb-2">Full Logo - Light</h3>
            <p className="text-gray-400 text-sm mb-4">Logo with text on white background</p>
            <button
              onClick={() => downloadLogoWithOptions(true, 'white', sizes[selectedSize])}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
            >
              Download PNG
            </button>
          </div>

          {/* Logo with Text - Transparent */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex justify-center items-center h-40 bg-gray-700 rounded-lg mb-4" style={{
              backgroundImage: 'repeating-conic-gradient(#808080 0% 25%, transparent 0% 50%)',
              backgroundPosition: '0 0, 10px 10px',
              backgroundSize: '20px 20px'
            }}>
              <Logo width={60} height={60} showText={true} textClassName="text-white" />
            </div>
            <h3 className="text-white font-semibold mb-2">Full Logo - Transparent</h3>
            <p className="text-gray-400 text-sm mb-4">Logo with text, transparent background</p>
            <button
              onClick={() => downloadLogoWithOptions(true, 'transparent', sizes[selectedSize])}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
            >
              Download PNG
            </button>
          </div>
        </div>

        {/* Size Selector */}
        <div className="mt-12 bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-white font-semibold mb-4">Select Download Size</h3>
          <div className="flex gap-4">
            <button
              onClick={() => setSelectedSize('small')}
              className={`flex-1 py-3 px-4 rounded-lg transition-colors ${
                selectedSize === 'small' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Small (256px)
            </button>
            <button
              onClick={() => setSelectedSize('medium')}
              className={`flex-1 py-3 px-4 rounded-lg transition-colors ${
                selectedSize === 'medium' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Medium (512px)
            </button>
            <button
              onClick={() => setSelectedSize('large')}
              className={`flex-1 py-3 px-4 rounded-lg transition-colors ${
                selectedSize === 'large' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Large (1024px)
            </button>
          </div>
        </div>

        {/* Quick Download Section */}
        <div className="mt-8 text-center">
          <p className="text-gray-400 mb-4">Quick download with default settings:</p>
          <button
            onClick={() => downloadLogoWithOptions(false, 'transparent', 512)}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 px-8 rounded-lg transition-all transform hover:scale-105 shadow-lg"
          >
            Download Default Logo (512px PNG)
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogoDownload;